import { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Table } from 'react-bootstrap';
import { DefinitionList } from '@/components/common/DefinitionList';
import { useTranslation } from 'react-i18next';
import { HiLink } from 'react-icons/hi2';
import { useDateFormat } from '@/utils/dates';
import { PageContent } from '@/components/layout/PageLayout';
import DetailHeader from '@/components/layout/DetailHeader';
import { Section, SectionTitle } from '@/components/layout/Toolbar.styled';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { InlineEdit } from '@/components/common/InlineEdit';
import type { InlineEditVariant } from '@/components/common/InlineEdit';
import { useGetCustomerQuery, useUpdateCustomerMutation } from '@/store/customersApi';
import { useListSprawyQuery } from '@/store/sprawyApi';
import GenerateUploadLinkModal from '@/components/customers/GenerateUploadLinkModal';
import CustomerUploadLinksList from '@/components/customers/CustomerUploadLinksList';

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const { data: customer, isLoading } = useGetCustomerQuery(id!, { skip: !id });
  const { data: sprawy } = useListSprawyQuery({ customerId: id, pageSize: 50 }, { skip: !id });
  const [showUploadLinkModal, setShowUploadLinkModal] = useState(false);
  const [updateCustomer] = useUpdateCustomerMutation();

  const handleSaveField = useCallback(async (field: string, value: string | number | boolean) => {
    await updateCustomer({
      id: id!,
      name: customer!.name,
      firstName: customer!.firstName ?? undefined,
      lastName: customer!.lastName ?? undefined,
      email: customer!.email ?? undefined,
      phone: customer!.phone ?? undefined,
      address: customer!.address ?? undefined,
      pesel: customer!.pesel ?? undefined,
      notes: customer!.notes ?? undefined,
      [field]: value || undefined,
    }).unwrap();
  }, [id, customer, updateCustomer]);

  if (isLoading || !customer) {
    return <PageContent><LoadingSpinner /></PageContent>;
  }

  const infoRows: Array<{ label: string; field: string; value: string | null | undefined; variant?: InlineEditVariant; required?: boolean }> = [
    { label: t('customers.form.name'), field: 'name', value: customer.name, required: true },
    { label: t('customers.form.firstName'), field: 'firstName', value: customer.firstName },
    { label: t('customers.form.lastName'), field: 'lastName', value: customer.lastName },
    { label: t('customers.form.email'), field: 'email', value: customer.email, variant: 'email' },
    { label: t('customers.form.phone'), field: 'phone', value: customer.phone },
    { label: t('customers.form.address'), field: 'address', value: customer.address },
    { label: t('customers.form.pesel'), field: 'pesel', value: customer.pesel },
    { label: t('customers.form.notes'), field: 'notes', value: customer.notes, variant: 'textarea' },
  ];

  return (
    <PageContent>
      <DetailHeader
        onBack={() => navigate(-1)}
        title={customer.name}
        meta={<span>{formatDate(customer.createdAt)}</span>}
        actions={
          <Button variant="outline-primary" size="sm" onClick={() => setShowUploadLinkModal(true)}>
            <HiLink size="1rem" className="me-1" />
            {t('customerUploadLink.generateButton')}
          </Button>
        }
      />

      <Section>
        <SectionTitle>{t('customers.detail.info')}</SectionTitle>
        <DefinitionList
          items={infoRows.map((r) => ({
            label: r.label,
            hideIfEmpty: false,
            value: (
              <InlineEdit
                value={r.value ?? null}
                onSave={(val) => handleSaveField(r.field, val)}
                variant={r.variant ?? 'text'}
                required={r.required}
                ariaLabel={r.label}
              />
            ),
          }))}
        />
      </Section>

      <Section>
        <SectionTitle>{t('customers.detail.linkedSprawy')}</SectionTitle>
        {!sprawy?.items.length ? (
          <p className="text-muted">{t('sprawy.detail.noDocuments')}</p>
        ) : (
          <Table hover responsive>
            <thead>
              <tr>
                <th>{t('sprawy.table.signature')}</th>
                <th>{t('sprawy.table.court')}</th>
                <th>{t('sprawy.table.portalStatus')}</th>
                <th>{t('sprawy.table.createdAt')}</th>
              </tr>
            </thead>
            <tbody>
              {sprawy.items.map((s) => (
                <tr key={s.id}>
                  <td><Link to={`/sprawy/${s.id}`}>{s.signature}</Link></td>
                  <td>{s.court ?? '—'}</td>
                  <td>{t(`sprawaStatus.${s.portalStatus}`)}</td>
                  <td>{formatDate(s.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section>
        <SectionTitle>{t('customerUploadLink.sectionTitle')}</SectionTitle>
        <CustomerUploadLinksList customerId={id!} />
      </Section>

      <GenerateUploadLinkModal
        show={showUploadLinkModal}
        onHide={() => setShowUploadLinkModal(false)}
        customerId={id!}
        customerEmail={customer.email}
        sprawy={sprawy?.items.filter((s) => s.signature != null).map((s) => ({ id: s.id, signature: s.signature! })) ?? []}
      />
    </PageContent>
  );
}
